import {Box, Button, Grid, MenuItem, Paper, TextField, Typography, Accordion, AccordionSummary, AccordionDetails} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import axios from 'axios'
import dayjs from 'dayjs'
import {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {useHistory, useParams} from 'react-router-dom'
import {MoonLoader} from 'react-spinners'
import {toast} from 'react-toastify'
import DynamicForm from '../../../../reusable-components/forms/DynamicForm'
import FormWrapper from '../../../../reusable-components/Wrapper/Wrapper'
import {BEHAVIOUR_NOTE_FORM_INPUTS, BEHAVIOUR_NOTE_FORM_INPUTS_DATA} from '../BEHAVIOUR_CONSTANTS'
import {RootState} from '../../../../../setup/redux/RootReducer'
import FrequencyDateRangePicker from '../../../../reusable-components/NoteType/NoteType'
const selectTimeZone = (state: RootState) => state.timeZone.timeZone

export default function NewBehaviourNote() {
  const {id, clientId, noteType}: {id: string, clientId: string, noteType: string} = useParams()
  const history = useHistory()
  const [isFormLoading, setIsFormLoading] = useState(false)
  const [uiElements, setUiElements] = useState([])
  const [formData, setFormData] = useState<any>({})
  const [noteData, setNoteData] = useState({
    noteType: noteType,
  })
  const [data, setData] = useState({
    client: {},
    behaviors: [],
  })
  const [behaviorFormData, setBehaviorFormData] = useState([])
  const timeZone = useSelector(selectTimeZone)
  const authUser = useSelector((state: {auth: any}) => state.auth)
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsFormLoading(true)
        const [clientResponse, noteResponse] = await Promise.all([
          axios.get('/clients/getClientNames'),
          axios.get('/progressNotes/getByClientId', {
            params: {
              clientId: clientId,
              assessmentId: id,
              noteType: noteType
            },
          }),
        ])
        const {data: clientData} = clientResponse
        const responseData = noteResponse?.data?.result[0]
        const {data} = clientData
        const updatedclients = [
          ...data.map((client) => ({label: client.fullName, value: client._id})),
        ]
        const updatedFormData = BEHAVIOUR_NOTE_FORM_INPUTS.map((item) => {
          if (item.name === 'clientId') {
            return {...item, options: updatedclients, disabled: true, value: clientId}
          }
          return item
        })
        const prevFormData = {
          ...responseData,
          dangerTo: responseData?.dangerTo || [],
          status: responseData?.status || '',
          responseToTx: responseData?.responseToTx || '',
          appearance: responseData?.appearance || '',
          judgment: responseData?.judgment || '',
          insights: responseData?.insights || '',
          speech: responseData?.speech || '',
          mood: responseData?.mood || '',
          behavior: responseData?.behaviour || '',
          thoughtContent: responseData?.thoughtContent || '',
          thoughtProcess: responseData?.thoughtProcess || '',
          orientation: responseData?.orientation || '',
          affect: responseData?.affect || '',
          therapeuticInterventions: responseData?.therapeuticInterventions || [],
          location: responseData?.historyOfIllness?.location || '',
          duration: responseData?.historyOfIllness?.duration || '',
          timing: responseData?.historyOfIllness?.timing || '',
          associatedSymptoms: responseData?.historyOfIllness?.associatedSymptoms || '',
          medicalHx: responseData?.historyOfIllness?.medicalHx || '',
          goalsObjectives: responseData?.goalsObjectives || '',
        }
        if (responseData?.behaviorsMapping) {
          const behaviorsMapping = responseData?.behaviorsMapping
          const behaviorsData = behaviorsMapping && behaviorsMapping?.map(({ targetId, replacementIds, _id }) => {
            return {
              isEdit: false,
              id: _id,
              targetBehavior: targetId?.name,
              targetBehaviorId: targetId?._id,
              targetRating: '',
              targetDescription: '',
              replacementBehavior: replacementIds.map((replacement) => ({
                _id: replacement?._id,
                name: replacement?.name,
                type: replacement?.type,
                rating: '',
                description: ''
              })),
            }
          })
          setData({
            client: responseData?.clientId,
            behaviors: behaviorsData || [],
          })
          setBehaviorFormData(behaviorsData)
        }
        setFormData(prevFormData)
        setUiElements(updatedFormData)
      } catch (error) {
        console.error('Error fetching clients:', error)
      } finally {
        setIsFormLoading(false)
      }
    }
    fetchClients()
  }, [])

  const handleSubmitForm = async (formValues) => {
    const userId = authUser.user._id
    formValues['clientId'] = clientId
    const requiredFields = [
      'clientId',
      'dangerTo',
      'status',
      'responseToTx',
      'appearance',
      'judgment',
      'insights',
      'speech',
      'mood',
      'behavior',
      'thoughtContent',
      'thoughtProcess',
      'orientation',
      'affect',
      'nextAppointment',
      'location',
      'duration',
      'timing',
      'associatedSymptoms',
      'medicalHx',
    ]

    for (const field of requiredFields) {
      if (!formValues[field] || (typeof formValues[field] === 'string' && !formValues[field].trim())) {
        return toast.error(`"${field}" is required`)
      }
    }
    const applyCurrentTime = (date, now) =>
      dayjs(date).hour(now.hour()).minute(now.minute()).second(0).millisecond(0)
    const now = dayjs()
    const formattedNextAppt = applyCurrentTime(formValues['nextAppointment'], now)
      .tz(timeZone, true)
      .toDate()

    const currentDate = dayjs()
    if (dayjs(formattedNextAppt).isBefore(currentDate, 'day')) {
      toast.error('Appointment cannot be in the past.')
      return
    }
    // setIsFormLoading(true)
    try {
      const payload = {
        clientId: clientId,
        assessmentId: id,
        programId: formValues['programId'],
        noteType: noteType,
        startDate: noteData['startDate'],
        timeIn: noteData['timeIn'] ? dayjs(noteData['timeIn'], 'hh:mm A').format('hh:mm A') : '',
        timeOut: noteData['timeOut'] ? dayjs(noteData['timeOut'], 'hh:mm A').format('hh:mm A') : '',
        endDate: noteData['endDate'],
        dangerTo: formValues['dangerTo'],
        status: formValues['status'],
        responseToTx: formValues['responseToTx'],
        appearance: formValues['appearance'],
        judgment: formValues['judgment'],
        insights: formValues['insights'],
        speech: formValues['speech'],
        mood: formValues['mood'],
        behaviour: formValues['behavior'],
        thoughtContent: formValues['thoughtContent'],
        thoughtProcess: formValues['thoughtProcess'],
        orientation: formValues['orientation'],
        affect: formValues['affect'],
        therapeuticInterventions: formValues['therapeuticInterventions'],
        historyOfIllness: {
          location: formValues.location,
          duration: formValues.duration,
          timing: formValues.timing,
          associatedSymptoms: formValues.associatedSymptoms,
          medicalHx: formValues.medicalHx,
        },
        goalsObjectives: formValues['goalsObjectives'],
        evidenceOfResponse: formValues['evidenceOfResponse'],
        interventionStudies: formValues['interventionStudies'],
        sessionContent: formValues['sessionContent'],
        futureTreatment: formValues['futureTreatment'],
        assignedHomework: formValues['assignedHomework'],
        nextAppointment: formattedNextAppt,
        therapistSignature: userId,
        behaviorMapping: behaviorFormData[0]?.targetRating ? behaviorFormData : [],
        isPdfGenerated: false,
      }
      const res = await axios.post('/progressNotes/create', payload)

      const {message, success} = res?.data

      if (!success) {
        return toast.error(message)
      }

      toast.success(message)
      history.push(`/notes/behavior-notes/list/${id}/${clientId}/${noteType}`)
    } catch (e) {
      toast.error(e?.response?.data?.error || `Couldn't Add Note`)
      console.error('Error adding behavior note:', e)
    } finally {
      setIsFormLoading(false)
    }
  }

  const buttons = [
    {label: 'Cancel', variant: 'outlined', type: 'button'},
    {label: 'Save', variant: 'contained', type: 'submit'},
  ]

  
  const handleChange = ({ target: { value, name } }, index) => {
    const updatedBehaviorFormData = [...behaviorFormData]
    updatedBehaviorFormData[index][name] = value
    setBehaviorFormData(updatedBehaviorFormData)
  }

  const handleReplacementChange = ({ target: { value, name } }, replacementIndex, index) => {
    const updatedBehaviorFormData = [...behaviorFormData]
    updatedBehaviorFormData[index].replacementBehavior[replacementIndex][name] = value
    setBehaviorFormData(updatedBehaviorFormData)
  }

  return (
    <FormWrapper
      title='Behavior Note'
      content={
        <>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
              marginLeft: '1rem',
            }}
            color='primary'
          >
            Note Type
          </Typography>
          <FrequencyDateRangePicker
            isEntry={true}
            isTemplate={false}
            isFullForm={false}
            isEdit={false}
            formData={noteData}
            setFormData={setNoteData}
          />
          {noteType === "DAILY" && behaviorFormData?.map((behavior, index) => (
            <Accordion key={behavior.id} sx={{ mb: 2, border: 'none', width: '97%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                }}
              >
                <Typography variant='h6' sx={{ fontWeight: 600, color: '#3f51b5' }}>
                  Behavior {index + 1}: {behavior.targetBehavior}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={8} sm={8} md={8}>
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        border: '1px solid #ccc',
                        borderRadius: 2,
                        backgroundColor: '#f7f7f7',
                      }}
                    >
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: '#3f51b5',
                            }}
                          >
                            Target Behavior
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#333',
                              fontStyle: 'italic',
                              p: 1,
                              borderRadius: 1,
                              marginLeft: "7rem"
                            }}
                          >
                            {behavior.targetBehavior}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4}>
                    <TextField
                      fullWidth
                      select
                      label='Rating'
                      name='targetRating'
                      variant='outlined'
                      value={behavior.targetRating || ''}
                      onChange={(e) => handleChange(e, index)}
                    >
                      <MenuItem value="0">0</MenuItem>
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="3">3</MenuItem>
                      <MenuItem value="4">4</MenuItem>
                      <MenuItem value="5">5</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Description'
                      name='targetDescription'
                      variant='outlined'
                      multiline
                      rows={3}
                      value={behavior.targetDescription}
                      onChange={(e) => handleChange(e, index)}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {behavior.replacementBehavior.map((replacement, replacementIndex) => (
                      <Box key={replacementIndex} sx={{ mb: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={8} sm={8} md={8}>
                            <Box
                              sx={{
                                mb: 1,
                                p: 1,
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <Grid container alignItems="center">
                                <Grid item>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#3f51b5',
                                    }}
                                  >
                                    Replacement Behavior
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: '#333',
                                      fontStyle: 'italic',
                                      p: 1,
                                      borderRadius: 1,
                                      marginLeft: "4rem"
                                    }}
                                  >
                                    {replacement.name}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={4} md={4}>
                            <TextField
                              fullWidth
                              select
                              label='Rating'
                              name='rating'
                              variant='outlined'
                              value={replacement.rating || ''}
                              onChange={(e) => handleReplacementChange(e, replacementIndex, index)}
                            >
                              <MenuItem value="0">0</MenuItem>
                              <MenuItem value="1">1</MenuItem>
                              <MenuItem value="2">2</MenuItem>
                              <MenuItem value="3">3</MenuItem>
                              <MenuItem value="4">4</MenuItem>
                              <MenuItem value="5">5</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                        <TextField
                          fullWidth
                          label='Description'
                          name='description'
                          variant='outlined'
                          multiline
                          rows={3}
                          value={replacement.description}
                          onChange={(e) => handleReplacementChange(e, replacementIndex, index)}
                        />
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
          {isFormLoading ? (
            <div
              className='overlay d-flex align-middle justify-content-center align-middle'
              style={{marginTop: '100px'}}
            >
              <MoonLoader color='#9db2fc' size={60} loading={isFormLoading} />
            </div>
          ) : (
            <>
              <DynamicForm
                fields={uiElements}
                data={formData}
                onSubmit={handleSubmitForm}
                buttons={buttons}
                setFormValues={setFormData}
              />
               <Box>
    
  </Box>
            </>
          )}
        </>
      }
    />
  )
}
